import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    ArrowLeftIcon,
    UserIcon,
    BuildingOfficeIcon,
    CalendarIcon,
    EnvelopeIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClipboardDocumentCheckIcon,
    ClipboardDocumentListIcon,
    WrenchScrewdriverIcon,
    ArrowPathIcon,
    ArrowUturnLeftIcon,
    ChatBubbleBottomCenterTextIcon,
    ChatBubbleLeftIcon,
    EyeIcon,
    MinusCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    ChevronDownIcon,
    PlusIcon,
    PencilIcon,
    BriefcaseIcon,
    BellIcon
} from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as CheckCircleIconSolid,
    ClockIcon as ClockIconSolid,
    ArrowPathIcon as ArrowPathIconSolid,
    ArrowUturnLeftIcon as ArrowUturnLeftIconSolid,
    XCircleIcon as XCircleIconSolid,
    MinusCircleIcon as MinusCircleIconSolid,
    ExclamationCircleIcon as ExclamationCircleIconSolid
} from '@heroicons/react/24/solid';
import {
    OnboardingRequestsClient,
    OnboardingRequestPreviewDto,
    RequestStatus,
    RequestItemsClient,
    RequestItemStatus,
    UpdateRequestItemStatusRequest,
    AssignAssetRequest,
    ReassignRequestItemRequest,
    AddRemarkRequest,
    TaskPhase,
    ApprovalStatus,
    ApprovalsClient
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { useUserEmail } from '../../hooks/useUserEmail';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import { getStatusBadgeColor, getRequestStatusText } from '../../utils/enumHelpers';
import { RequestStatusBadge, ItemStatusBadge, TaskPhaseBadge } from '../../components/ui/status-badge';
import { ERROR_MESSAGES, SUCCESS_MESSAGES, LOADING_TEXT } from '../../constants/messages';
import AssetModal from '../../components/checklist/AssetModal';
import ReassignModal from '../../components/checklist/ReassignModal';
import ActivityLogModal from '../../components/checklist/ActivityLogModal';
import AddRemarkModal from '../../components/checklist/AddRemarkModal';
import AddTaskModal, { AddTaskData } from '../../components/checklist/AddTaskModal';
import { getErrorMessage } from '../../utils/errorHelpers';

// Component to display assigned team and person
function AssignmentBadges({ assignedTeamName, assignedPersonName }: { assignedTeamName?: string | null, assignedPersonName?: string | null }) {
    if (!assignedTeamName && !assignedPersonName) return null;

    return (
        <div className="flex flex-wrap gap-2">
            {assignedTeamName && (
                <span className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                    👥 {assignedTeamName}
                </span>
            )}
            {assignedPersonName && (
                <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                    👤 {assignedPersonName}
                </span>
            )}
        </div>
    );
}

export default function OnboardingRequestDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [request, setRequest] = useState<OnboardingRequestPreviewDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    // Modal states
    const [assetModal, setAssetModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; currentAsset?: string }>({ isOpen: false });
    const [reassignModal, setReassignModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; currentPicId?: string; currentPicName?: string }>({ isOpen: false });
    const [activityLogModal, setActivityLogModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [remarkModal, setRemarkModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string; mode?: 'remark' | 'rejection' | 'reset' | 'returned' }>({ isOpen: false });
    const [addTaskModal, setAddTaskModal] = useState<boolean>(false);
    const [confirmCompleteModal, setConfirmCompleteModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [pendingStatusChange, setPendingStatusChange] = useState<{ itemId: number; newStatus: RequestItemStatus } | null>(null);
    const [updateStatusModal, setUpdateStatusModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [openUpdateStatusDropdownId, setOpenUpdateStatusDropdownId] = useState<number | null>(null);

    // Dropdown state for status selection
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

    // Loading states for async operations
    const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
    const [assigningAssetId, setAssigningAssetId] = useState<number | null>(null);
    const [reassigningItemId, setReassigningItemId] = useState<number | null>(null);
    const [addingRemarkId, setAddingRemarkId] = useState<number | null>(null);
    const [sendingReminderId, setSendingReminderId] = useState<number | null>(null);
    const [sendingAllReminders, setSendingAllReminders] = useState(false);
    const [showRemindConfirmModal, setShowRemindConfirmModal] = useState<{ isOpen: boolean; itemId?: number; itemName?: string }>({ isOpen: false });
    const [showRemindAllConfirmModal, setShowRemindAllConfirmModal] = useState(false);

    // Decline modal states
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [declining, setDeclining] = useState(false);

    useEffect(() => {
        const fetchRequestDetails = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const client = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                const data = await client.getOnboardingRequestPreview(parseInt(id));
                setRequest(data);
            } catch (error) {
                console.error(ERROR_MESSAGES.FETCH_FAILED('request details'), error);
                toast.error(ERROR_MESSAGES.FETCH_FAILED('request details'));
            } finally {
                setLoading(false);
            }
        };

        fetchRequestDetails();
    }, [id, refreshKey]);

    // Handler functions for checklist features
    const handleStatusUpdate = async (itemId: number, newStatus: RequestItemStatus, remarks?: string) => {
        try {
            setUpdatingStatusId(itemId);
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.updateRequestItemStatus(itemId, new UpdateRequestItemStatusRequest({
                status: newStatus,
                remarks: remarks
            }));
            toast.success(SUCCESS_MESSAGES.STATUS_UPDATED);
            setRefreshKey(k => k + 1);
        } catch (error) {
            console.error(ERROR_MESSAGES.STATUS_UPDATE_FAILED, error);
            toast.error(ERROR_MESSAGES.STATUS_UPDATE_FAILED);
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleStatusClick = (itemId: number, itemName: string, newStatus: RequestItemStatus) => {
        setOpenDropdownId(null);

        // If changing to Completed, show confirmation
        if (newStatus === RequestItemStatus.Completed) {
            setConfirmCompleteModal({ isOpen: true, itemId, itemName });
            setPendingStatusChange({ itemId, newStatus });
            return;
        }

        // If changing to Rejected, force remark input
        if (newStatus === RequestItemStatus.Rejected) {
            setRemarkModal({ isOpen: true, itemId, itemName, mode: 'rejection' });
            setPendingStatusChange({ itemId, newStatus });
            return;
        }

        // For other statuses, update directly
        handleStatusUpdate(itemId, newStatus);
    };

    const handleConfirmComplete = () => {
        if (pendingStatusChange) {
            handleStatusUpdate(pendingStatusChange.itemId, pendingStatusChange.newStatus);
            setPendingStatusChange(null);
        }
        setConfirmCompleteModal({ isOpen: false });
    };

    const handleAssetAssign = async (assetName: string, assetValue?: string) => {
        if (!assetModal.itemId) return;

        try {
            setAssigningAssetId(assetModal.itemId);
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.assignAsset(assetModal.itemId, new AssignAssetRequest({
                assetName,
                assetValue
            }));
            toast.success(SUCCESS_MESSAGES.ASSET_ASSIGNED);
            setRefreshKey(k => k + 1);
            setAssetModal({ isOpen: false });
        } catch (error) {
            console.error(ERROR_MESSAGES.ASSET_ASSIGN_FAILED, error);
            toast.error(ERROR_MESSAGES.ASSET_ASSIGN_FAILED);
        } finally {
            setAssigningAssetId(null);
        }
    };

    const handleReassign = async (newPicId: string, reason?: string) => {
        if (!reassignModal.itemId) return;

        try {
            setReassigningItemId(reassignModal.itemId);
            const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.reassignRequestItem(reassignModal.itemId, new ReassignRequestItemRequest({
                newPicId,
                reason
            }));
            toast.success(SUCCESS_MESSAGES.TASK_REASSIGNED);
            setRefreshKey(k => k + 1);
            setReassignModal({ isOpen: false });
        } catch (error) {
            console.error(ERROR_MESSAGES.REASSIGN_FAILED, error);
            toast.error(ERROR_MESSAGES.REASSIGN_FAILED);
        } finally {
            setReassigningItemId(null);
        }
    };

    const handleAddRemark = async (remark: string) => {
        if (!remarkModal.itemId) return;

        try {
            setAddingRemarkId(remarkModal.itemId);

            // If this is for a status change (rejection, reset, or returned), update status with remark
            if (pendingStatusChange) {
                await handleStatusUpdate(pendingStatusChange.itemId, pendingStatusChange.newStatus, remark);
                const statusName = pendingStatusChange.newStatus === RequestItemStatus.Rejected ? 'rejected' :
                                   pendingStatusChange.newStatus === RequestItemStatus.Pending ? 'reset to pending' :
                                   pendingStatusChange.newStatus === RequestItemStatus.Returned ? 'returned' : 'updated';
                setPendingStatusChange(null);
                toast.success(`Status updated to ${statusName} with remark`);
            } else {
                // Otherwise, just add the remark
                const client = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
                await client.addRequestItemRemark(remarkModal.itemId, new AddRemarkRequest({
                    remark
                }));
                toast.success(SUCCESS_MESSAGES.REMARK_ADDED);
                setRefreshKey(k => k + 1);
            }

            setRemarkModal({ isOpen: false });
        } catch (error) {
            console.error(ERROR_MESSAGES.REMARK_ADD_FAILED, error);
            toast.error(ERROR_MESSAGES.REMARK_ADD_FAILED);
        } finally {
            setAddingRemarkId(null);
        }
    };

    const handleAddTask = async (taskData: AddTaskData) => {
        if (!id) return;

        try {
            console.log('Sending task data:', taskData);
            const response = await fetch(`${API_CONFIG.baseUrl}/api/onboardingrequests/${id}/add-task`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(taskData)                                                                                                          
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to add task: ${response.status} - ${errorText}`);
            }

            toast.success(SUCCESS_MESSAGES.POST_ONBOARDING_TASK_ADDED);
            setRefreshKey(k => k + 1);
            setAddTaskModal(false);
        } catch (error) {
            console.error('Failed to add task:', error);
            throw error;
        }
    };

    // Handle send reminder for a single task - show confirmation first
    const handleRemindClick = (itemId: number, itemName: string) => {
        setShowRemindConfirmModal({ isOpen: true, itemId, itemName });
    };

    // Confirm and send reminder for a single task
    const handleConfirmRemind = async () => {
        if (!showRemindConfirmModal.itemId) return;

        const itemId = showRemindConfirmModal.itemId;
        setShowRemindConfirmModal({ isOpen: false });

        try {
            setSendingReminderId(itemId);
            const response = await fetch(`${API_CONFIG.baseUrl}/api/requestitems/${itemId}/remind`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to send reminder');
            }

            toast.success(SUCCESS_MESSAGES.REMINDER_SENT);
        } catch (error: unknown) {
            console.error('Failed to send reminder:', error);
            toast.error(getErrorMessage(error, 'Failed to send reminder'));
        } finally {
            setSendingReminderId(null);
        }
    };

    // Handle remind all click - show confirmation first
    const handleRemindAllClick = () => {
        setShowRemindAllConfirmModal(true);
    };

    // Confirm and send reminders for all pending tasks
    const handleConfirmRemindAll = async () => {
        if (!id) return;

        setShowRemindAllConfirmModal(false);

        try {
            setSendingAllReminders(true);
            const response = await fetch(`${API_CONFIG.baseUrl}/api/onboardingrequests/${id}/remind-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to send reminders');
            }

            const result = await response.json();
            toast.success(SUCCESS_MESSAGES.REMINDERS_SENT(result.remindersSent));
        } catch (error: unknown) {
            console.error('Failed to send reminders:', error);
            toast.error(getErrorMessage(error, 'Failed to send reminders'));
        } finally {
            setSendingAllReminders(false);
        }
    };

    // Handle decline request
    const handleDecline = async () => {
        if (!declineReason.trim()) {
            toast.error('Please provide a reason for declining');
            return;
        }

        try {
            setDeclining(true);

            const response = await fetch(`${API_CONFIG.baseUrl}/api/onboardingrequests/${id}/decline`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ reason: declineReason })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to decline request');
            }

            const result = await response.json();
            setShowDeclineModal(false);

            // Navigate to offboarding details page
            toast.success(SUCCESS_MESSAGES.ONBOARDING_DECLINED);
            navigate(`/offboarding/${result.offboardingRequestId}`);
        } catch (error: unknown) {
            console.error('Failed to decline request:', error);
            toast.error(getErrorMessage(error, 'Failed to decline request'));
        } finally {
            setDeclining(false);
        }
    };

    const [resendingApproval, setResendingApproval] = useState(false);

    const handleResendApprovalEmail = async (approvalId: number) => {
        if (!window.confirm('Resend the approval email? This will generate a new link and invalidate the previous one.')) return;

        setResendingApproval(true);
        try {
            const client = new ApprovalsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.resendApprovalEmail(approvalId);
            toast.success('Approval email resent successfully');
        } catch (err: unknown) {
            toast.error(getErrorMessage(err, 'Failed to resend approval email'));
        } finally {
            setResendingApproval(false);
        }
    };

    const handleResendItemApprovalEmail = async (itemApprovalId: number) => {
        if (!window.confirm('Resend the item approval email? This will generate a new link and invalidate the previous one.')) return;

        setResendingApproval(true);
        try {
            const client = new ApprovalsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
            await client.resendItemApprovalEmail(itemApprovalId);
            toast.success('Item approval email resent successfully');
        } catch (err: unknown) {
            toast.error(getErrorMessage(err, 'Failed to resend item approval email'));
        } finally {
            setResendingApproval(false);
        }
    };

    // Check if Decline button should be shown
    const showDeclineButton = request?.status === RequestStatus.PendingFirstApproval ||
        request?.status === RequestStatus.PendingSecondApproval ||
        request?.status === RequestStatus.Approved;

    const getItemStatusBadge = (status: RequestItemStatus) => {
        const baseClasses = "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg shadow-sm";

        switch (status) {
            case RequestItemStatus.Pending:
                return (
                    <span className={`${baseClasses} bg-yellow-100 text-yellow-800 border-2 border-yellow-400`}>
                        <ClockIconSolid className="h-5 w-5" />
                        Pending
                    </span>
                );
            case RequestItemStatus.Completed:
                return (
                    <span className={`${baseClasses} bg-green-100 text-green-800 border-2 border-green-500`}>
                        <CheckCircleIconSolid className="h-5 w-5" />
                        Completed
                    </span>
                );
            case RequestItemStatus.Rejected:
                return (
                    <span className={`${baseClasses} bg-red-100 text-red-800 border-2 border-red-400`}>
                        <XCircleIconSolid className="h-5 w-5" />
                        Rejected
                    </span>
                );
            case RequestItemStatus.Returned:
                return (
                    <span className={`${baseClasses} bg-teal-100 text-teal-800 border-2 border-teal-400`}>
                        <ArrowUturnLeftIconSolid className="h-5 w-5" />
                        Returned
                    </span>
                );
            default:
                return (
                    <span className={`${baseClasses} bg-gray-100 text-gray-800 border-2 border-gray-300`}>
                        <ExclamationCircleIcon className="h-5 w-5" />
                        Unknown
                    </span>
                );
        }
    };

    const getStatusOptions = () => [
        {
            value: RequestItemStatus.Pending,
            label: 'Pending',
            icon: ClockIconSolid,
            className: 'text-yellow-800'
        },
        {
            value: RequestItemStatus.Completed,
            label: 'Completed',
            icon: CheckCircleIconSolid,
            className: 'text-green-800'
        },
        {
            value: RequestItemStatus.Rejected,
            label: 'Rejected',
            icon: XCircleIconSolid,
            className: 'text-red-800'
        }
    ];

    const renderStatusDropdown = (itemId: number, itemName: string, currentStatus: RequestItemStatus, requiresApproval?: boolean, approvalStatus?: ApprovalStatus) => {
        const isOpen = openDropdownId === itemId;
        const statusOptions = getStatusOptions();
        const currentOption = statusOptions.find(opt => opt.value === currentStatus);
        const isUpdating = updatingStatusId === itemId;
        const isFinalized = currentStatus === RequestItemStatus.Completed || currentStatus === RequestItemStatus.Rejected || currentStatus === RequestItemStatus.Returned;

        // Check if task requires approval and is not yet approved
        const isAwaitingApproval = requiresApproval && approvalStatus !== ApprovalStatus.Approved && approvalStatus !== ApprovalStatus.Rejected;

        // If finalized, show the badge with cursor-not-allowed
        if (isFinalized) {
            return (
                <div className="inline-block cursor-not-allowed" title="Status is finalized and cannot be changed">
                    {getItemStatusBadge(currentStatus)}
                </div>
            );
        }

        // If awaiting approval, show disabled button with appropriate message
        if (isAwaitingApproval) {
            return (
                <div className="inline-block cursor-not-allowed" title="Cannot change status until task is approved">
                    <button
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-lg cursor-not-allowed opacity-60"
                    >
                        <ClockIcon className="h-5 w-5" />
                        <span>Awaiting Approval</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="relative inline-block">
                <button
                    onClick={() => !isUpdating && setOpenDropdownId(isOpen ? null : itemId)}
                    disabled={isUpdating}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isUpdating ? (
                        <div className="animate-spin h-5 w-5 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                    ) : (
                        currentOption && (
                            <currentOption.icon className={`h-5 w-5 ${currentOption.className}`} />
                        )
                    )}
                    <span>{isUpdating ? 'Updating...' : 'Change Status'}</span>
                    {!isUpdating && (
                        <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    )}
                </button>

                {isOpen && (
                    <>
                        {/* Backdrop to close dropdown when clicking outside */}
                        <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenDropdownId(null)}
                        />

                        {/* Dropdown menu */}
                        <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
                            {statusOptions.map((option) => {
                                const isCurrentStatus = option.value === currentStatus;
                                return (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            if (!isCurrentStatus) {
                                                handleStatusClick(itemId, itemName, option.value);
                                            } else {
                                                setOpenDropdownId(null);
                                            }
                                        }}
                                        disabled={isCurrentStatus}
                                        className={`
                                            w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
                                            ${isCurrentStatus
                                                ? 'bg-gray-100 cursor-not-allowed'
                                                : 'hover:bg-gray-50 cursor-pointer'
                                            }
                                            ${option.value === RequestItemStatus.Pending ? 'rounded-t-lg' : ''}
                                            ${option.value === RequestItemStatus.Rejected ? 'rounded-b-lg' : ''}
                                            border-b border-gray-100 last:border-b-0
                                        `}
                                    >
                                        <option.icon className={`h-5 w-5 ${option.className} flex-shrink-0`} />
                                        <span className={`font-medium ${option.className}`}>
                                            {option.label}
                                        </span>
                                        {isCurrentStatus && (
                                            <CheckCircleIcon className="h-4 w-4 ml-auto text-blue-600" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Render Update Status dropdown for Completed tasks
    const renderUpdateStatusDropdown = (itemId: number, itemName: string) => {
        const isOpen = openUpdateStatusDropdownId === itemId;

        return (
            <div className="relative inline-block">
                <button
                    onClick={() => setOpenUpdateStatusDropdownId(isOpen ? null : itemId)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors shadow-sm"
                >
                    <ArrowPathIcon className="h-5 w-5" />
                    <span>Update Status</span>
                    <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                    <>
                        {/* Backdrop to close dropdown when clicking outside */}
                        <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenUpdateStatusDropdownId(null)}
                        />

                        {/* Dropdown menu */}
                        <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                            <button
                                onClick={() => {
                                    setOpenUpdateStatusDropdownId(null);
                                    setPendingStatusChange({ itemId, newStatus: RequestItemStatus.Pending });
                                    setRemarkModal({ isOpen: true, itemId, itemName, mode: 'reset' });
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer rounded-t-lg border-b border-gray-100"
                            >
                                <ArrowPathIcon className="h-5 w-5 text-orange-600 flex-shrink-0" />
                                <div className="text-left">
                                    <span className="font-medium text-orange-800">Reset Status</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Revert to Pending for re-processing</p>
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    setOpenUpdateStatusDropdownId(null);
                                    setPendingStatusChange({ itemId, newStatus: RequestItemStatus.Returned });
                                    setRemarkModal({ isOpen: true, itemId, itemName, mode: 'returned' });
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 cursor-pointer rounded-b-lg"
                            >
                                <ArrowUturnLeftIcon className="h-5 w-5 text-teal-600 flex-shrink-0" />
                                <div className="text-left">
                                    <span className="font-medium text-teal-800">Mark as Returned</span>
                                    <p className="text-xs text-gray-500 mt-0.5">Item returned, won't track in offboarding</p>
                                </div>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Check if all tasks are finalized (Completed, Rejected, or Returned)
    const areAllTasksCompleted = () => {
        if (!request) return false;

        const allItems = request.categories?.flatMap(c => c.items || []) || [];

        if (allItems.length === 0) return false;

        return allItems.every(item =>
            item.status === RequestItemStatus.Completed || item.status === RequestItemStatus.Rejected || item.status === RequestItemStatus.Returned
        );
    };


    if (loading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">{LOADING_TEXT.LOADING_DETAILS}</div>
                    </div>
                </div>
            </div>
        );
    }

    if (!request) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex flex-col items-center justify-center py-12">
                        <XCircleIcon className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-gray-600 font-medium">Request not found</p>
                        <button
                            onClick={() => navigate('/onboarding')}
                            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to Requests
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                    <button
                        onClick={() => navigate('/onboarding')}
                        className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeftIcon className="mr-2 h-4 w-4" />
                        Back to Requests
                    </button>
                    <div className="flex flex-col items-end gap-2">
                        {request.status !== undefined && request.status !== null && (
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(request.status)}`}>
                                {getRequestStatusText(request.status)}
                            </span>
                        )}
                        {/* Progress Indicator - shown for Approved/InProgress/Completed statuses */}
                        {(request.status === RequestStatus.Approved || request.status === RequestStatus.InProgress || request.status === RequestStatus.Completed) && (() => {
                            const totalTasks = request.totalTasks || 0;
                            const completedTasks = request.completedTasks || 0;
                            const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

                            return (
                                <div className="w-48">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs font-medium text-gray-600">Progress</span>
                                        <span className="text-xs font-semibold text-gray-700">{progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                            className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 rounded-full transition-all duration-300"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{completedTasks} / {totalTasks} tasks</p>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                    Onboarding Request Details
                </h1>
            </div>

            {/* Employee Information */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <UserIcon className="h-6 w-6 mr-2 text-green-600" />
                    Employee Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.fullName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BriefcaseIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Designation</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.designation}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.locationName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Department</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.departmentName}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Personal Email</label>
                            <p className="text-base font-semibold text-gray-900">{request.employee?.personalEmail}</p>
                        </div>
                    </div>
                    <div className="flex items-start">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                        <div>
                            <label className="block text-sm font-medium text-gray-500 mb-1">Employment Date</label>
                            <p className="text-base font-semibold text-gray-900">
                                {request.employee?.employmentDate ? new Date(request.employee.employmentDate + 'Z').toLocaleDateString() : '-'}
                            </p>
                        </div>
                    </div>
                    {request.employee?.immediateSuperiorEmail && (
                        <div className="flex items-start">
                            <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-500 mb-1">Immediate Superior</label>
                                <p className="text-base font-semibold text-gray-900">{request.employee.immediateSuperiorEmail}</p>
                                {request.firstApprovalDate ? (
                                    <p className="text-sm text-green-600 mt-1 flex items-center">
                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                        Approved: {new Date(request.firstApprovalDate + 'Z').toLocaleDateString()}
                                    </p>
                                ) : request.firstApprovalId && (request.status === RequestStatus.PendingFirstApproval) ? (
                                    <button
                                        onClick={() => handleResendApprovalEmail(request.firstApprovalId!)}
                                        disabled={resendingApproval}
                                        className="mt-1 inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                                    >
                                        <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                                        {resendingApproval ? 'Sending...' : 'Resend Approval Email'}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    )}
                    {request.employee?.reportingSuperiorEmail && (
                        <div className="flex items-start">
                            <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-500 mb-1">Second Level Approver Email</label>
                                <p className="text-base font-semibold text-gray-900">{request.employee.reportingSuperiorEmail}</p>
                                {request.secondApprovalDate ? (
                                    <p className="text-sm text-green-600 mt-1 flex items-center">
                                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                                        Approved: {new Date(request.secondApprovalDate + 'Z').toLocaleDateString()}
                                    </p>
                                ) : request.secondApprovalId && (request.status === RequestStatus.PendingSecondApproval) ? (
                                    <button
                                        onClick={() => handleResendApprovalEmail(request.secondApprovalId!)}
                                        disabled={resendingApproval}
                                        className="mt-1 inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                                    >
                                        <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                                        {resendingApproval ? 'Sending...' : 'Resend Approval Email'}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Onboarding Tasks */}
            <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                        <ClipboardDocumentListIcon className="h-6 w-6 mr-2 text-blue-600" />
                        Onboarding Tasks
                    </h2>
                    <div className="flex items-center gap-3">
                        {/* Remind All - Only show when request is Approved/InProgress/Completed and there are pending tasks */}
                        {(request.status === RequestStatus.Approved || request.status === RequestStatus.InProgress || request.status === RequestStatus.Completed) && (() => {
                            const pendingTaskCount = request.categories?.flatMap(c => c.items || [])
                                .filter(item => item.status !== RequestItemStatus.Completed &&
                                               item.status !== RequestItemStatus.Rejected &&
                                               item.status !== RequestItemStatus.Returned).length || 0;

                            if (pendingTaskCount > 0) {
                                return (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">
                                            {pendingTaskCount} Upcoming Task{pendingTaskCount !== 1 ? 's' : ''}
                                        </span>
                                        <button
                                            onClick={handleRemindAllClick}
                                            disabled={sendingAllReminders}
                                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <BellIcon className="h-4 w-4 mr-1" />
                                            {sendingAllReminders ? 'Sending...' : 'Remind All'}
                                        </button>
                                    </div>
                                );
                            }
                            return null;
                        })()}
                        {/* Add Task button only shows in post-onboarding phase (when request is completed) */}
                        {request.status === RequestStatus.Completed && (
                            <button
                                onClick={() => setAddTaskModal(true)}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                <PlusIcon className="h-5 w-5 mr-1" />
                                Add Task
                            </button>
                        )}
                    </div>
                </div>

                {/* Conditional rendering based on approval status */}
                {request.status === RequestStatus.Approved || request.status === RequestStatus.InProgress || request.status === RequestStatus.Completed ? (
                    /* Interactive Checklist View - After Approval, InProgress or Completed */
                    <>
                        {/* Categorized Tasks with Interactive Features */}
                        {request.categories && request.categories.length > 0 && (
                            <div className="space-y-6">
                                {request.categories.map((category) => (
                                    <div key={category.categoryId} className="border rounded-xl overflow-visible">
                                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3">
                                            <h3 className="text-lg font-semibold text-white">{category.categoryName}</h3>
                                        </div>
                                        <div className="divide-y divide-gray-200">
                                            {category.items?.map((item) => (
                                                <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="text-lg font-medium text-gray-900">{item.itemName}</h4>
                                                                {item.phase === TaskPhase.PostOnboarding && (
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                                        Post-Onboarding
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {item.description && (
                                                                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1">
                                                            {getItemStatusBadge(item.status ?? RequestItemStatus.Pending)}
                                                            {/* Remind button - show only for non-completed tasks */}
                                                            {item.status !== RequestItemStatus.Completed &&
                                                             item.status !== RequestItemStatus.Rejected &&
                                                             item.status !== RequestItemStatus.Returned && (
                                                                <button
                                                                    onClick={() => handleRemindClick(item.id!, item.itemName ?? '')}
                                                                    disabled={sendingReminderId === item.id}
                                                                    className="text-xs text-amber-600 hover:text-amber-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                                >
                                                                    <BellIcon className="h-3 w-3" />
                                                                    {sendingReminderId === item.id ? 'Sending...' : 'Remind'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                        <div className="flex items-center text-sm">
                                                            <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                            <span className="text-gray-600">Assignee:</span>
                                                            <span className="ml-2 font-medium text-gray-900">
                                                                {item.assignedTeamName ? `👥 ${item.assignedTeamName}` :
                                                                 item.assignedPersonName ? `👤 ${item.assignedPersonName}` :
                                                                 'Unassigned'}
                                                            </span>
                                                        </div>
                                                        {item.assignedAsset && (
                                                            <div className="flex items-center text-sm">
                                                                <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                                <span className="ml-2 font-medium text-gray-900">{item.assignedAsset}</span>
                                                            </div>
                                                        )}
                                                        {item.latestRemark && (
                                                            <div className="flex flex-col text-sm">
                                                                <div className="flex items-center">
                                                                    <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400 mr-2" />
                                                                    <span className="text-gray-600">Latest Remark:</span>
                                                                    <span className="ml-2 text-gray-900">{item.latestRemark}</span>
                                                                </div>
                                                                {item.latestRemarkDate && (
                                                                    <div className="text-xs text-gray-500 italic mt-1 ml-6">
                                                                        Updated on {new Date(item.latestRemarkDate + 'Z').toLocaleString()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Approval Information */}
                                                    {item.requiresApproval && (
                                                        <div className="mb-4">
                                                            {item.approvalStatus === ApprovalStatus.Rejected ? (
                                                                <div className="space-y-2">
                                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                        <XCircleIconSolid className="h-4 w-4" />
                                                                        Rejected by {item.approverEmail}
                                                                    </span>
                                                                    {item.rejectionReason && (
                                                                        <div className="text-sm text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">
                                                                            <span className="font-semibold">Reason: </span>
                                                                            {item.rejectionReason}
                                                                        </div>
                                                                    )}
                                                                    {item.rejectionDate && (
                                                                        <div className="text-xs text-red-600">
                                                                            Rejected on {new Date(item.rejectionDate + 'Z').toLocaleString()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : item.approvalStatus === ApprovalStatus.Approved ? (
                                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    <CheckCircleIconSolid className="h-4 w-4" />
                                                                    Approved by {item.approverEmail}
                                                                </span>
                                                            ) : (
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                        <ClockIconSolid className="h-4 w-4" />
                                                                        Pending Approval
                                                                        {item.approverEmail && ` from ${item.approverEmail}`}
                                                                    </span>
                                                                    {item.itemApprovalId && (
                                                                        <button
                                                                            onClick={() => handleResendItemApprovalEmail(item.itemApprovalId!)}
                                                                            disabled={resendingApproval}
                                                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                                                                        >
                                                                            <EnvelopeIcon className="h-3 w-3 mr-1" />
                                                                            {resendingApproval ? 'Sending...' : 'Resend'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Action Buttons */}
                                                    {(() => {
                                                        const isFinalized = item.status === RequestItemStatus.Completed || item.status === RequestItemStatus.Rejected || item.status === RequestItemStatus.Returned;
                                                        const isCompleted = item.status === RequestItemStatus.Completed;
                                                        const isPending = item.status === RequestItemStatus.Pending;
                                                        const isRequestCompleted = request.status === RequestStatus.Completed;
                                                        const isPostOnboarding = item.phase === TaskPhase.PostOnboarding;

                                                        // Show Change Status if:
                                                        // 1. Request is not completed, OR
                                                        // 2. It's a post-onboarding task, OR
                                                        // 3. Task is pending (may have been reset and needs to be completed again)
                                                        const showChangeStatus = !isRequestCompleted || isPostOnboarding || isPending;

                                                        return (
                                                            <div className="flex flex-wrap gap-2">
                                                                {/* Status Dropdown - Show based on conditions above */}
                                                                {showChangeStatus && renderStatusDropdown(item.id!, item.itemName ?? '', item.status ?? RequestItemStatus.Pending, item.requiresApproval, item.approvalStatus)}

                                                                {/* Update Status Dropdown - Only show for Completed tasks after the onboarding flow is complete */}
                                                                {isCompleted && isRequestCompleted && renderUpdateStatusDropdown(item.id!, item.itemName ?? '')}

                                                                {/* Asset Button - Always available */}
                                                                <button
                                                                    onClick={() => setAssetModal({
                                                                        isOpen: true,
                                                                        itemId: item.id,
                                                                        itemName: item.itemName ?? '',
                                                                        currentAsset: item.assignedAsset ?? undefined
                                                                    })}
                                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                                                                >
                                                                    <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
                                                                    {item.assignedAsset ? 'Update Asset' : 'Assign Asset'}
                                                                </button>

                                                                {/* Reassign Button - Only for non-finalized tasks when status changes are allowed */}
                                                                {!isFinalized && showChangeStatus && (
                                                                    <button
                                                                        onClick={() => setReassignModal({
                                                                            isOpen: true,
                                                                            itemId: item.id,
                                                                            itemName: item.itemName ?? '',
                                                                            currentPicId: item.picId ?? undefined,
                                                                            currentPicName: item.assignedPersonName ?? undefined
                                                                        })}
                                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                                                                    >
                                                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                                                        Reassign
                                                                    </button>
                                                                )}

                                                                {/* Add Remark Button - Always available */}
                                                                <button
                                                                    onClick={() => setRemarkModal({
                                                                        isOpen: true,
                                                                        itemId: item.id,
                                                                        itemName: item.itemName ?? ''
                                                                    })}
                                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                                                                >
                                                                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-1" />
                                                                    Add Remark
                                                                </button>

                                                                {/* Activity Log Button - Always available */}
                                                                <button
                                                                    onClick={() => setActivityLogModal({
                                                                        isOpen: true,
                                                                        itemId: item.id,
                                                                        itemName: item.itemName ?? ''
                                                                    })}
                                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                                                                >
                                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                                    View History
                                                                </button>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    /* Simple View - Before Approval */
                    <>
                        {/* Categories */}
                        {request.categories && request.categories.length > 0 ? (
                            <div className="space-y-6">
                                {request.categories.map((category) => (
                                    <div key={category.categoryId} className="border border-gray-200 rounded-xl overflow-hidden">
                                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
                                            <h3 className="font-semibold text-gray-900 text-lg">{category.categoryName}</h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {category.items?.length || 0} {category.items?.length === 1 ? 'task' : 'tasks'}
                                            </p>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {category.items && category.items.map((item, idx) => (
                                                <div key={idx} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                                    <div className="flex items-start">
                                                        <div className="flex-shrink-0 mt-1">
                                                            {item.status === RequestItemStatus.Completed ? (
                                                                <CheckCircleIconSolid className="h-6 w-6 text-green-600" />
                                                            ) : (
                                                                <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                                                            )}
                                                        </div>
                                                        <div className="ml-4 flex-1">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <p className={`text-base font-medium ${item.status === RequestItemStatus.Completed ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                                                                        {item.itemName}
                                                                    </p>
                                                                    {item.phase === TaskPhase.PostOnboarding && (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                                                            Post-Onboarding
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <AssignmentBadges
                                                                    assignedTeamName={item.assignedTeamName}
                                                                    assignedPersonName={item.assignedPersonName}
                                                                />
                                                            </div>
                                                            {item.description && (
                                                                <p className="text-sm text-gray-600 mt-2">{item.description}</p>
                                                            )}
                                                            {item.dueDate && (
                                                                <div className="flex items-center mt-2 text-sm text-orange-600">
                                                                    <CalendarIcon className="h-4 w-4 mr-1" />
                                                                    Due: {new Date(item.dueDate + 'Z').toLocaleDateString()}
                                                                </div>
                                                            )}
                                                            {/* Approval Information */}
                                                            {item.requiresApproval && (
                                                                <div className="mt-2">
                                                                    {item.approvalStatus === ApprovalStatus.Rejected ? (
                                                                        <div className="space-y-2">
                                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                                <XCircleIconSolid className="h-4 w-4" />
                                                                                Rejected by {item.approverEmail}
                                                                            </span>
                                                                            {item.rejectionReason && (
                                                                                <div className="text-sm text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">
                                                                                    <span className="font-semibold">Reason: </span>
                                                                                    {item.rejectionReason}
                                                                                </div>
                                                                            )}
                                                                            {item.rejectionDate && (
                                                                                <div className="text-xs text-red-600">
                                                                                    Rejected on {new Date(item.rejectionDate + 'Z').toLocaleString()}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : item.approvalStatus === ApprovalStatus.Approved ? (
                                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            <CheckCircleIconSolid className="h-4 w-4" />
                                                                            Approved by {item.approverEmail}
                                                                        </span>
                                                                    ) : (
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                                <ClockIconSolid className="h-4 w-4" />
                                                                                Pending Approval
                                                                                {item.approverEmail && ` from ${item.approverEmail}`}
                                                                            </span>
                                                                            {item.itemApprovalId && (
                                                                                <button
                                                                                    onClick={() => handleResendItemApprovalEmail(item.itemApprovalId!)}
                                                                                    disabled={resendingApproval}
                                                                                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                                                                                >
                                                                                    <EnvelopeIcon className="h-3 w-3 mr-1" />
                                                                                    {resendingApproval ? 'Sending...' : 'Resend'}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </>
                )}

                {/* No tasks */}
                {(!request.categories || request.categories.length === 0) && (
                        <div className="text-center py-8">
                            <CheckCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">No tasks assigned yet</p>
                        </div>
                    )}

                {/* Auto-completion message - Show info that request will auto-complete */}
                {request.status === RequestStatus.Approved && areAllTasksCompleted() && (
                    <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                        <div className="flex items-center">
                            <CheckCircleIconSolid className="h-8 w-8 text-green-600 mr-3" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Onboarding Completed!</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    All onboarding tasks have been finalized. This request has been automatically marked as completed.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
                {/* Edit Button for Draft Status */}
                {request.status === RequestStatus.Draft && (
                    <button
                        onClick={() => navigate(`/onboarding/${id}/edit`)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                        <PencilIcon className="h-4 w-4 mr-1.5" />
                        Edit Request
                    </button>
                )}

                {/* Decline Button for Pending/Approved Status */}
                {showDeclineButton && (
                    <button
                        onClick={() => setShowDeclineModal(true)}
                        disabled={declining}
                        className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                        Decline
                    </button>
                )}
            </div>

            {/* Modals */}
            <AssetModal
                isOpen={assetModal.isOpen}
                onClose={() => setAssetModal({ isOpen: false })}
                onSubmit={handleAssetAssign}
                currentAsset={assetModal.currentAsset}
                taskName={assetModal.itemName ?? ''}
            />

            <ReassignModal
                isOpen={reassignModal.isOpen}
                onClose={() => setReassignModal({ isOpen: false })}
                onSubmit={handleReassign}
                currentPicId={reassignModal.currentPicId}
                currentPicName={reassignModal.currentPicName}
                taskName={reassignModal.itemName ?? ''}
            />

            <ActivityLogModal
                isOpen={activityLogModal.isOpen}
                onClose={() => setActivityLogModal({ isOpen: false })}
                requestItemId={activityLogModal.itemId ?? 0}
                taskName={activityLogModal.itemName ?? ''}
            />

            <AddRemarkModal
                isOpen={remarkModal.isOpen}
                onClose={() => {
                    setRemarkModal({ isOpen: false });
                    setPendingStatusChange(null);
                }}
                onSubmit={handleAddRemark}
                taskName={remarkModal.itemName ?? ''}
                mode={remarkModal.mode}
            />

            <AddTaskModal
                isOpen={addTaskModal}
                onClose={() => setAddTaskModal(false)}
                onSave={handleAddTask}
            />

            {/* Confirm Complete Modal */}
            {confirmCompleteModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Confirm Task Completion
                                </h3>
                                <p className="text-sm text-gray-600 mb-1">
                                    Are you sure you want to mark this task as completed?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => {
                                    setConfirmCompleteModal({ isOpen: false });
                                    setPendingStatusChange(null);
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmComplete}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Yes, Complete
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Remind Single Task Confirmation Modal */}
            {showRemindConfirmModal.isOpen && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <BellIcon className="h-8 w-8 text-amber-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Send Reminder
                                </h3>
                                <p className="text-sm text-gray-600">
                                    This will send an email to the assignee for task "<strong>{showRemindConfirmModal.itemName}</strong>". Are you sure you want to send a reminder?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowRemindConfirmModal({ isOpen: false })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmRemind}
                                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                            >
                                Yes, Send Reminder
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Remind All Tasks Confirmation Modal */}
            {showRemindAllConfirmModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <BellIcon className="h-8 w-8 text-amber-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Send Reminders to All Assignees
                                </h3>
                                <p className="text-sm text-gray-600">
                                    This will send an email to all assignees with pending tasks. Are you sure you want to remind all?
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end mt-6">
                            <button
                                onClick={() => setShowRemindAllConfirmModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmRemindAll}
                                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
                            >
                                Yes, Remind All
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Decline Confirmation Modal */}
            {showDeclineModal && createPortal(
                <div className="fixed inset-0 left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0">
                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">Decline Onboarding</h3>
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to decline this onboarding request for <strong>{request?.employee?.fullName}</strong>?
                        </p>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for declining <span className="text-red-500">*</span></label>
                            <textarea
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                rows={3}
                                placeholder="Please provide a reason for declining this request..."
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowDeclineModal(false);
                                    setDeclineReason('');
                                }}
                                disabled={declining}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDecline}
                                disabled={declining || !declineReason.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {declining ? 'Declining...' :
                                    'Confirm Decline'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
}
